import { useState } from "react";
import { useParams } from "react-router-dom";

import type { Question } from "../api/types";
import { useSubmitAnswer } from "../hooks/useSubmitAnswer";
import { useSurveyState } from "../hooks/useSurveyState";
import { ProgressBar } from "./ProgressBar";
import { SurveyExpired } from "./SurveyExpired";
import { SurveyIntro } from "./SurveyIntro";
import { SurveyQuestion } from "./SurveyQuestion";
import { SurveyThankYou } from "./SurveyThankYou";

export function SurveyPage() {
  const { token } = useParams<{ token: string }>();
  const [hasStarted, setHasStarted] = useState(false);

  const { data, isLoading, error } = useSurveyState(token);
  const submit = useSubmitAnswer(token);

  if (!token) return <SurveyExpired variant="not_found" />;
  if (isLoading) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center text-[1rem] text-[color:var(--color-wren-supporting)]">
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center text-[1rem] text-[color:var(--color-wren-supporting)]">
        Something glitched on our end. Try again in a moment?
      </div>
    );
  }
  if (!data) return null;

  if (data.state === "expired") return <SurveyExpired variant="expired" />;
  if (data.state === "not_found") return <SurveyExpired variant="not_found" />;
  if (data.state === "complete") return <SurveyThankYou />;

  const alreadyAnswered = Object.keys(data.answers_so_far ?? {}).length;
  const totalQuestions = (data.question_bank ?? []).length;

  if (!hasStarted && alreadyAnswered === 0) {
    return <SurveyIntro onStart={() => setHasStarted(true)} />;
  }

  const nextId = data.next_question_id;
  const nextQuestion: Question | undefined = (data.question_bank ?? []).find((q) => q.id === nextId);
  if (!nextQuestion) return <SurveyThankYou />;

  const indexInBank = (data.question_bank ?? []).findIndex((q) => q.id === nextId);

  return (
    <div className="mx-auto flex min-h-screen min-h-dvh max-w-md flex-col px-6 py-12">
      <ProgressBar current={indexInBank + 1} total={totalQuestions} />
      <SurveyQuestion
        // Remount on every question so per-question local state (e.g. the
        // free-text textarea draft) doesn't bleed into the next question.
        key={nextQuestion.id}
        question={nextQuestion}
        disabled={submit.isPending}
        onAnswer={(value) =>
          submit.mutate({ question_id: nextQuestion.id, value })
        }
      />
    </div>
  );
}
